import type { EditorStore } from './EditorStore'
import type { EditorShape, ShapeId } from './types'

export function findShapeAncestorImpl(
  store: EditorStore,
  shape: EditorShape,
  predicate: (s: EditorShape) => boolean,
): EditorShape | undefined {
  let current: EditorShape | undefined = shape
  while (current) {
    if (predicate(current)) return current
    current = current.parentId ? store.shapes.get(current.parentId) : undefined
  }
  return undefined
}

export function reparentShapesImpl(
  store: EditorStore,
  shapeIds: ShapeId[],
  newParentId: ShapeId,
): void {
  const prevParents = shapeIds.map(id => {
    const shape = store.shapes.get(id)
    return { id, parentId: shape?.parentId }
  })

  store.history.execute({
    do: () => {
      for (const id of shapeIds) {
        const shape = store.shapes.get(id)
        if (shape) {
          store.shapes.set(id, { ...shape, parentId: newParentId })
        }
      }
      store.notify()
    },
    undo: () => {
      for (const { id, parentId } of prevParents) {
        const shape = store.shapes.get(id)
        if (shape) {
          store.shapes.set(id, { ...shape, parentId })
        }
      }
      store.notify()
    },
  })
}

export function unparentShapesImpl(
  store: EditorStore,
  shapeIds: ShapeId[],
): void {
  const prevParents = shapeIds.map(id => {
    const shape = store.shapes.get(id)
    return { id, parentId: shape?.parentId }
  })

  store.history.execute({
    do: () => {
      for (const id of shapeIds) {
        const shape = store.shapes.get(id)
        if (shape && shape.parentId !== undefined) {
          const updated = { ...shape }
          delete updated.parentId
          store.shapes.set(id, updated)
        }
      }
      store.notify()
    },
    undo: () => {
      for (const { id, parentId } of prevParents) {
        const shape = store.shapes.get(id)
        if (shape) {
          store.shapes.set(id, { ...shape, parentId })
        }
      }
      store.notify()
    },
  })
}

export function clearPageImpl(store: EditorStore): void {
  const allShapes = [...store.shapes.entries()]
  store.history.execute({
    do: () => {
      store.shapes.clear()
      store.selectedShapeIds.clear()
      store.notify()
    },
    undo: () => {
      for (const [id, shape] of allShapes) {
        store.shapes.set(id, shape)
      }
      store.notify()
    },
  })
}
